import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '@common/hooks/redux';
import { useRequest } from '@common/services/api';
import { AlertService } from '@common/services/alert';
import { selected } from '@modules/organisation/selectors/organisation';
import {
  approveAccessRequest,
  getAccessRequestsEndpoint,
  rejectAccessRequest,
} from './access-requests-api';
import {
  APIResponseAccessRequestsList,
  UseAccessRequestsReturn,
} from './types';
import { useAccessRequestsContext } from '@modules/social/contexts/access-requests-context';

export const useAccessRequests = (
  channelId?: string
): UseAccessRequestsReturn => {
  const { t } = useTranslation();
  const [loadingActions, setLoadingActions] = useState<Set<string>>(new Set());
  const selectedOrganisation = useAppSelector(selected);
  const params = useParams<{ id: string }>();
  const { setRemoveRequest } = useAccessRequestsContext();
  // Use provided channelId or fall back to URL params
  const activeChannelId = channelId || params.id;

  const { data, setData, loading, refetch } =
    useRequest<APIResponseAccessRequestsList>(
      getAccessRequestsEndpoint(selectedOrganisation.id, activeChannelId)
    );

  useEffect(() => {
    if (activeChannelId) {
      refetch();
    }
  }, [activeChannelId]);

  const handleAccessRequest = async (accessRequestId: string, action: 'approve' | 'reject') => {
    const actionKey = `${accessRequestId}-${action}`;

    try {
      setLoadingActions((prev) => new Set(prev).add(actionKey));

      if (action === 'approve') {
        await approveAccessRequest(selectedOrganisation.id, accessRequestId);
        AlertService.success(t('social:access_request_approved_successfully'));
      } else {
        await rejectAccessRequest(selectedOrganisation.id, accessRequestId);
        AlertService.success(t('social:access_request_rejected_successfully'));
      }

      // Remove the request from local data immediately for better UX
      setData(data.filter((request) => request.id !== accessRequestId));
      setRemoveRequest(accessRequestId);
      setLoadingActions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(actionKey);
        return newSet;
      });
    } catch (error: any) {
      const messageKey = action === 'approve' ? 'approved' : 'rejected';
      AlertService.forStatus(error.status, {
        warning: t(`social:access_request_${messageKey}_warning`),
        error: t(`social:access_request_${messageKey}_error`),
      });
      setLoadingActions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(actionKey);
        return newSet;
      });
    }
  };

  const handleApprove = (accessRequestId: string) => handleAccessRequest(accessRequestId, 'approve');
  const handleReject = (accessRequestId: string) => handleAccessRequest(accessRequestId, 'reject');

  const isActionLoading = (accessRequestId: string, action: 'approve' | 'reject') => {
    return loadingActions.has(`${accessRequestId}-${action}`);
  };

  const accessRequests = useMemo(() => {
    return data?.filter((request) => request.status === 'pending') ?? [];
  }, [data]);

  return {
    accessRequests,
    setAccessRequests: setData,
    loading,
    isActionLoading,
    handleApprove,
    handleReject,
    refetch,
  };
};
